//Stores
import { req_locked_devices } from 'src/stores/device-runtime/local-auth';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import {
	get_device_component_index,
	get_gen_2_component_channel_structure,
	get_local_ip,
	is_gen_2,
	is_gen_z,
	isCctOnlyDevice,
	isProThreeEM,
	isRgbCct,
	isRgbwwPm,
	isXMODDevice,
	real_id,
} from '../../../helpers/interface_helpers';
import { getXzComponentId } from 'src/services/xz_http_reqs';
import { applyXCSpecToStatus, getJWSFromDevice } from '../../../helpers/xmod_helpers/xcspec_helpers';
import {
	addXT1DeviceToGroupRequest,
	extractJRPCGroupCommands,
	getXT1LocalGroupRequests,
	type JRPCCommand,
} from 'src/helpers/xt1_helpers/template_group_helpers';
import { localReq, postReq } from '../../../services/http';

//Helpers
import { RELAY_LIGHT_CONTROL } from '../../../constants';
import { devices_config } from '@devices/index';
import { requestSaveGroup } from 'src/components/Lists/list-helpers';
import { isXT1ComponentDevice, isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
//Services
import { format } from 'svelte-i18n';
import { gen2Request } from '../../../services/gen_2_http_reqs';
import { get } from 'svelte/store';
//Desing system components
import { toast } from '@Shelly/shelly-design-system';
//Types
import type { Device } from 'src/types/Device';
import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';
import type { VirtualComponentTypes } from 'src/types/VirtualComponents';

const _format = get(format);

const updateStatuses = (res, status, device, isGen2, type?) => {
	if (typeof res.ison !== 'undefined') {
		if (isGen2) {
			status[get_gen_2_component_channel_structure(device)].output = res.ison;
		} else {
			if (device.category === 'emeter') {
				status['relays'][0].ison = res.ison;
			} else if (device?.type.includes('SW') && type !== 'roller') {
				status['relays'][device.channel].ison = res.ison;
			} else if (type === 'roller') {
				status['relays'][device.channel].ison = res.ison;
			} else {
				status[device.category + 's'][device.channel].ison = res.ison;
			}
		}
	} else if (res.hasOwnProperty('state')) {
		status['rollers'][device.channel].state = res.state;
	} else if (res.hasOwnProperty('last_direction')) {
	}

	devices_status.update_device_status({
		id: device.id,
		msg: status,
	});
};

//#region requests

/**
 * Function to send a request to the cloud for a group of devices.
 * Using the iotU v2 API
 */
export async function cloudGroupRequestV2(
	url: string,
	data,
	commands,
	timeout = 10000,
	handle_error = true,
) {
	if (typeof data !== 'object') {
		return;
	}

	const groupReqData = {};
	let jrpcReqData: JRPCCommand[] = [];

	// Add command property for each group
	Object.keys(data)?.forEach((element) => {
		if (element === 'light' || element === 'switch' || element === 'cover') {
			groupReqData[element] = { ...data[element], ...{ command: commands } };
		} else {
			jrpcReqData = [
				...jrpcReqData,
				...extractJRPCGroupCommands(element as VirtualComponentTypes, data[element], commands),
			];
		}
	});

	try {
		const responses: CloudApiResponse[] = [];
		if (Object.keys(groupReqData).length) {
			responses.push(await postReq(url, groupReqData, timeout, undefined, undefined, true));
		}
		if (jrpcReqData.length) {
			responses.push(
				await postReq('/v2/devices/jrpc', jrpcReqData, timeout, undefined, false, true),
			);
		}
		if (!responses.length) return;

		const res = {
			isok: responses.every((response) => response?.isok),
			error: responses.find((response) => response?.error)?.error,
			failedCommands: {
				...responses[0]?.failedCommands,
				...responses[1]?.failedCommands,
			},
			data: responses,
		};

		if (res.error && handle_error) {
			toast.open({ type: 'danger', title: _format('error_global') });
			return;
		}

		return res;
	} catch (error) {
		if (handle_error) {
			toast.open({ type: 'danger', title: _format('error_global') });
		}
	}
}

export async function cloudGroupRequest(url, groupDevices, more, timeout = 6000, handle_error = true) {
	const res: any = await postReq(url, { devices: JSON.stringify(groupDevices), ...more }, timeout);

	if (res?.isok) {
		return res;
	} else {
		if (typeof res?.errors !== 'undefined') {
			if (typeof res?.errors['control_failed'] !== 'undefined') {
				const failedCount = res?.errors['control_failed'].split(' ')[0];

				toast.open({
					type: 'danger',
					title: `Control failed for ${failedCount} device.`,
				});
			}
		} else {
			if (handle_error) {
				toast.open({ type: 'danger', title: _format('error_global') });
			}
		}
	}
}

export async function localGroupRequest(device, data, method = 'post', group?) {
	const status = devices_status.getByRealId(device.id);
	const isGen2 = is_gen_2(device);
	let res;
	if (isXT1Module(device?.type) && !isXT1ComponentDevice(device)) {
		const responses = [];
		const requests = getXT1LocalGroupRequests(device, data);
		for (const request of requests) {
			responses.push(await gen2Request(device, request.method, request.params));
		}
		res = {
			isok: responses.length > 0 && responses.every((response) => response?.isok),
			data: responses,
		};
	} else if (isGen2) {
		res = await gen2Request(device, method, { id: device.channel, ...data });
	} else {
		const url = `${device.ip}/${
			RELAY_LIGHT_CONTROL.includes(device?.type) && group?.type !== 'roller'
				? 'relay'
				: group?.type === 'roller'
					? 'roller'
					: device?.category
		}/${device.channel}`;

		res = await localReq(device, url, method, data, 6000, !isGen2);
	}

	if (res.isok) {
		updateStatuses(res, status, device, isGen2, group?.type);
	} else {
		toast.open({ type: 'danger', title: _format('error_global') });
	}

	return res;
}
//#endregion

//#region list manipulations
// changed this, because devices can be object when is one device or array if more devices
export function handleCloudLocalLists(groupDevices) {
	const _devices = get(devices);
	const cloud = {} as Device;
	const local = {} as Device;
	if (Array.isArray(groupDevices)) {
		groupDevices = [...new Set((groupDevices ?? []).map(String))];
		for (const deviceId of groupDevices) {
			let baseDeviceId = deviceId;
			let channelFromSuffix = null;
			if (deviceId.includes('_')) {
				const idMatch = deviceId.match(/^(.+)_(\d+)$/);
				if (idMatch) {
					baseDeviceId = idMatch[1];
					channelFromSuffix = parseInt(idMatch[2], 10);
				}
			}
			const channelDevice = _devices[deviceId];
			const parentDevice = _devices[baseDeviceId];
			const device = channelDevice || parentDevice;
			if (device) {
				const _real_id = real_id(baseDeviceId);
				let channel = isProThreeEM(device?.type) ? 100 : device.channel;
				let channelIndex = channel || 0;
				if (isXMODDevice(device?.type) || isRgbwwPm(device?.type)) {
					if (channelFromSuffix !== null) {
						channelIndex = channelFromSuffix;
					} else if (channelDevice?.channel !== undefined) {
						channelIndex = channelDevice.channel;
					}
					channel = channelIndex;
					channel = get_device_component_index({ ...device, channel: channelIndex });
					if (
						isRgbwwPm(device?.type) &&
						['rgbcct', 'rgbx2light'].includes(device.rgbww_profile)
					) {
						channel = device.channel;
					}
				}
				const status = get(devices_status);
				let type = device.type;
				if (isCctOnlyDevice(device?.type)) {
					type = 'cct';
				} else if (isRgbCct(device?.type)) {
					type = 'rgbcct';
				} else if (isXMODDevice(device?.type)) {
					const deviceStatus = status?.[_real_id];
					if (deviceStatus?.sys?.ch && Array.isArray(deviceStatus.sys.ch)) {
						const component = deviceStatus.sys.ch[channelIndex] || deviceStatus.sys.ch[0];
						if (component) {
							type = component.split(':')[0];
						}
					}
				}
				const deviceKey = deviceId;
				if (status?.[_real_id]?.online) {
					// prevent sending commands to devices with safety
					if (!status?.['cb:0']?.safety) {
						cloud[deviceKey] = {
							id: _real_id,
							type: type,
							channel,
							gen: device.gen,
							category: device.category,
							mode: device.mode,
							component: device.component,
							service_type: device.service_type,
						};
					}
				} else {
					let local_ip = get_local_ip(_real_id);
					if (local_ip) {
						delete cloud[deviceKey];
						// prevent sending commands to devices with safety
						if (!status?.['cb:0']?.safety) {
							local[deviceKey] = {
								id: _real_id,
								type: type,
								channel,
								gen: device.gen,
								ip: local_ip,
								category: device.category,
								mode: device.mode,
								component: device.component,
								service_type: device.service_type,
							};
						}
					}
				}
			}
		}
	} else {
		if (typeof _devices[groupDevices?.id] !== 'undefined') {
			const device = _devices[groupDevices.id];
			const _real_id = real_id(device.id);
			const channel = isProThreeEM(device?.type) ? 100 : device.channel;
			if (devices_status.getByRealId(_real_id)?.online) {
				cloud[device.id] = {
					id: _real_id,
					type: device.type,
					channel: device.channel,
					gen: device.gen,
					category: device.category,
					mode: device.mode,
					component: device.component,
					service_type: device.service_type,
				};
			} else {
				let local_ip: { ip?: string } = get_local_ip(_real_id);
				if (local_ip) {
					delete cloud[device.id];
					local[device.id] = {
						id: _real_id,
						type: device.type,
						channel,
						gen: device.gen,
						ip: local_ip,
						category: device.category,
						mode: device.mode,
						component: device.component,
						service_type: device.service_type,
					};
				}
			}
		}
	}
	return { cloud, local };
}

export function groupSimilarFinder(group) {
	return group.devices.some((d) => get(req_locked_devices).includes(d));
}
//#endregion

// add/remove to the request locked list (special for TRV, we need to send one request at a time)
// ToDo:  those 2 function can be inside the store itself
export function addToRequestLockedList(devices_obj) {
	const devices: Device[] = Object.values(devices_obj);
	for (let device of devices) {
		req_locked_devices.add(device.id);
	}
}

export function removeFromRequestLockedList(devices_obj) {
	const devices: Device[] = Object.values(devices_obj);
	for (let device of devices) {
		req_locked_devices.remove(device.id);
	}
}

//#region color helpers
export const calculateRGB = (status) => {
	if (!status) return { r: 255, g: 0, b: 0 };

	if (typeof status.lights !== 'undefined') {
		//gen 1
		return {
			r: status.lights[0].red,
			g: status.lights[0].green,
			b: status.lights[0].blue,
		};
	} else {
		return {
			//gen 2++
			//old rgb limitation calculations
			// r: colorLimited(status[get_gen_2_component_channel_structure(get(devices)[status.id])].rgb[0], 'from'),
			// g: colorLimited(status[get_gen_2_component_channel_structure(get(devices)[status.id])].rgb[1], 'from'),
			// b: colorLimited(status[get_gen_2_component_channel_structure(get(devices)[status.id])].rgb[2], 'from')
			r: status[get_gen_2_component_channel_structure(get(devices)[status.id])].rgb[0],
			g: status[get_gen_2_component_channel_structure(get(devices)[status.id])].rgb[1],
			b: status[get_gen_2_component_channel_structure(get(devices)[status.id])].rgb[2],
		};
	}
};
//#endregion

//#region color temperature helpers
export function calculateTempRange(white_lights) {
	let minValues = [],
		maxValues = [];
	for (let d of white_lights) {
		// TODO: Refactoring!
		if (
			typeof devices_config[d?.type]?.modes?.white !== 'undefined' &&
			typeof devices_config[d?.type]?.modes?.white?.color_temp !== 'undefined'
		) {
			minValues.push(devices_config[d?.type]?.modes?.white?.color_temp.minValue);
			maxValues.push(devices_config[d?.type]?.modes?.white?.color_temp.maxValue);
		}

		if (
			typeof devices_config[d?.type]?.modes?.cct !== 'undefined' &&
			typeof devices_config[d?.type]?.modes?.cct?.color_temp !== 'undefined'
		) {
			//gen 3 component color temperature component

			minValues.push(devices_config[d?.type]?.modes?.cct?.color_temp.minValue);
			maxValues.push(devices_config[d?.type]?.modes?.cct?.color_temp.maxValue);
		}

		if (
			typeof devices_config[d?.type]?.modes?.rgbcct !== 'undefined' &&
			typeof devices_config[d?.type]?.modes?.rgbcct?.color_temp !== 'undefined'
		) {
			//gen 3 component color temperature component

			minValues.push(devices_config[d?.type]?.modes?.rgbcct?.color_temp.minValue);
			maxValues.push(devices_config[d?.type]?.modes?.rgbcct?.color_temp.maxValue);
		}

		if (
			typeof devices_config[d?.type]?.modes?.cctx2 !== 'undefined' &&
			typeof devices_config[d?.type]?.modes?.cctx2?.color_temp !== 'undefined'
		) {
			//gen 3 component color temperature component

			minValues.push(devices_config[d?.type]?.modes?.cctx2?.color_temp.minValue);
			maxValues.push(devices_config[d?.type]?.modes?.cctx2?.color_temp.maxValue);
		}

		//get the max value from min to set min value to the range (to avoid inappropriate kelvins for some lights)
		minValues.sort((a, b) => (a > b ? -1 : 1)); //returns [5000, 3000, 2700]

		//prepare the same check for lights could be with different max values,
		//but here we get the min value from all
		maxValues.sort((a, b) => (a > b ? 1 : -1)); //returns for ex [7000, 6500,6000]

		return [minValues[0], maxValues[0]];
	}
}
//#endregion

//#region update position helpers
export const updateGroupPosition = async (value: number, group) => {
	await requestSaveGroup({ ...group, slider: value }, true); //adding slider value to the group object https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2490
};
//#endregion

/**
 * Function to determine the type of device group based on the device category
 * @param deviceId Id of the device
 * @returns group type
 */
const getDeviceGroupType = (deviceId: string) => {
	if (!deviceId) return;

	const _devices = get(devices);
	let device = _devices[deviceId];
	if (!device && deviceId.includes('_')) {
		const baseId = deviceId.split('_')[0];
		device = _devices[baseId];
	}

	if (isXMODDevice(device?.type)) {
		if (device?.mode === 'light' || device?.mode === 'cct' || device?.mode === 'rgbw') {
			return 'light';
		}
		if (device?.mode === 'relay' || device?.mode === 'switch') {
			return 'switch';
		}
		const status = devices_status.getByRealId(real_id(device.id));
		if (status?.sys?.ch && Array.isArray(status.sys.ch)) {
			const idMatch = deviceId.match(/_(\d+)$/);
			const channelIndex = idMatch ? parseInt(idMatch[1], 10) : (device.channel ?? 0);
			const component = status.sys.ch[channelIndex] || status.sys.ch[0];
			if (component) {
				const componentType = component.split(':')[0];
				if (componentType === 'cct' || componentType === 'rgbw' || componentType === 'light') {
					return 'light';
				}
				if (componentType === 'switch') {
					return 'switch';
				}
			}
		}
		return 'switch';
	}

	switch (device?.category) {
		case 'light':
			return 'light';
		case 'module':
			if (isCctOnlyDevice(device?.type)) {
				return 'cct';
			}
			return 'switch';

		case 'roller':
		case 'cover':
			return 'cover';

		case 'switch':
		case 'relay':
		default:
			return 'switch';
	}
};

/**
 * Helper to process XMOD device components for group control
 * Uses xcspec library to ensure components are properly identified from JWS tokens.
 * @param fullDevice - The full device object
 * @param deviceStatus - The device status object
 * @param deviceId - The device ID from the devices list
 * @param type - The device group type (e.g., 'light', 'switch')
 * @param devicesList - The accumulating devices list object
 * @returns true if XMOD device was processed, false otherwise
 */
const processXMODDeviceComponents = (
	fullDevice: Device,
	deviceStatus: any,
	deviceId: string,
	type: string,
	devicesList: any,
): boolean => {
	if (!isXMODDevice(fullDevice?.type)) {
		return false;
	}

	if (!deviceStatus) {
		return false;
	}

	let components: string[] = deviceStatus?.sys?.ch || [];

	if (!components.length) {
		const settings = devices_settings.getByRealId(real_id(fullDevice.id));
		const jws = getJWSFromDevice(fullDevice, undefined, settings);

		if (jws) {
			try {
				applyXCSpecToStatus(jws, deviceStatus);
				components = deviceStatus?.sys?.ch || [];
			} catch (error) {
				console.error('[xcspec] Error applying xcspec in processXMODDeviceComponents:', error);
			}
		}
	}

	if (!components.length) {
		components = deviceStatus?.xcomps || [];
	}

	if (!components.length) {
		return false;
	}

	const expectedComponentType = type === 'switch' ? 'relay' : type;
	const processedIds = new Set<string>();

	if (!devicesList[type]) {
		devicesList[type] = { ids: [] };
	}

	for (const component of components) {
		if (!component) continue;

		const [componentType] = component.split(':');
		let normalizedComponentType = componentType === 'relay' ? 'switch' : componentType;

		if (
			normalizedComponentType === 'cct' ||
			normalizedComponentType === 'rgbw' ||
			normalizedComponentType === 'rgbcct'
		) {
			normalizedComponentType = 'light';
		}

		if (normalizedComponentType !== expectedComponentType) {
			continue;
		}

		const componentStatus = deviceStatus[component];
		const chnmIndex = componentStatus?._chnm;
		const componentId =
			Number.isFinite(chnmIndex) && chnmIndex > 0 ? `${deviceId}_${chnmIndex}` : deviceId;

		if (!processedIds.has(componentId) && !devicesList[type].ids.includes(componentId)) {
			devicesList[type].ids.push(componentId);
			processedIds.add(componentId);
		}
	}

	if (processedIds.size > 0) {
		const uniqueIds = new Set(devicesList[type].ids);
		devicesList[type].ids = Array.from(uniqueIds);
		return true;
	}

	return false;
};

/**
 * Helper to process SPDC-0D5PE16EU device components for group control
 * Collects all light components (rgb:0, cct:0, etc.) from status and uses their _chnm property
 * @param fullDevice - The full device object
 * @param deviceStatus - The device status object
 * @param deviceId - The device ID from the devices list
 * @param type - The device group type (e.g., 'light', 'switch')
 * @param devicesList - The accumulating devices list object
 * @returns true if SPDC-0D5PE16EU device was processed, false otherwise
 */
const processSPDCRGBWWComponents = (
	fullDevice: Device,
	deviceStatus: any,
	deviceFromList: Device,
	deviceId: string,
	type: string,
	devicesList: any,
): boolean => {
	if (!isRgbwwPm(fullDevice?.type)) {
		return false;
	}

	if (!deviceStatus || type !== 'light') {
		return false;
	}

	if (!devicesList[type]) {
		devicesList[type] = { ids: [] };
	}

	const processedIds = new Set<string>();
	const baseId = real_id(deviceId);

	// Use the device from the list's channel property to determine which channel we're processing
	// This ensures we use the correct channel even if fullDevice is the base device
	const targetChannel = deviceFromList?.channel ?? 0;

	const lightComponentTypes = ['rgb', 'cct', 'light'];
	for (const componentType of lightComponentTypes) {
		for (let i = 0; i < 5; i++) {
			const component = `${componentType}:${i}`;
			const componentStatus = deviceStatus[component];
			if (!componentStatus) continue;

			const chnmIndex = componentStatus?._chnm;
			// Only process components that match the device's channel
			if (Number.isFinite(chnmIndex) && chnmIndex === targetChannel) {
				// Construct the componentId with suffix for channels > 0, similar to processXMODDeviceComponents
				const componentId = chnmIndex > 0 ? `${baseId}_${chnmIndex}` : baseId;

				if (!processedIds.has(componentId) && !devicesList[type].ids.includes(componentId)) {
					devicesList[type].ids.push(componentId);
					processedIds.add(componentId);
				}
			}
		}
	}

	return processedIds.size > 0;
};

/**
 * Builds the device groups used in a group-control request.
 * XT1 service components are matched against the command names in `data`.
 *
 * @param devicesList - Devices to include in the request.
 * @param data - Command values keyed by command name.
 * @returns Devices grouped by request type, with their corresponding IDs.
 * @example { switch: { ids: ['XXXXX', 'XXXX'] }, cover: { ids: ['XXXXX'] } }
 * // Returns { switch: { ids: ['XXXXX', 'XXXX'] }, cover: { ids: ['XXXXX'] } }
 */
export const getDevicesFormatted = (
	devicesList: Device[],
	data: Record<string, string | number | boolean>,
) => {
	const _devices = get(devices);
	const tmpDevices = devicesList.reduce<Record<string, { ids: string[] }>>((devicesList, d) => {
		if (!d) return devicesList;

		const fullDevice =
			_devices[d.id] || Object.values(_devices).find((dev: Device) => real_id(dev.id) === d.id);

		if (isXT1Module(fullDevice?.type)) {
			return addXT1DeviceToGroupRequest(d, devicesList, data);
		}

		if (isXMODDevice(fullDevice?.type)) {
			const deviceStatus = devices_status.getByRealId(real_id(fullDevice.id));
			const type = getDeviceGroupType(d.id);

			if (processXMODDeviceComponents(fullDevice, deviceStatus, d.id, type, devicesList)) {
				return devicesList;
			}
		}

		if (isRgbwwPm(fullDevice?.type)) {
			const deviceStatus = devices_status.getByRealId(real_id(fullDevice.id));
			const type = getDeviceGroupType(d.id);
			const deviceFromList = _devices[d.channel > 0 ? `${d.id}_${d.channel}` : d.id];
			if (
				processSPDCRGBWWComponents(
					fullDevice,
					deviceStatus,
					deviceFromList,
					d.id,
					type,
					devicesList,
				)
			) {
				return devicesList;
			}
		}

		if (is_gen_z(fullDevice)) {
			const type = getDeviceGroupType(d.id);
			const baseId = real_id(d.id);
			const xzComponentId = getXzComponentId({
				...fullDevice,
				id: d.id,
				channel: d.channel ?? fullDevice.channel,
			});
			const id = `${baseId}_${xzComponentId}`;

			if (!devicesList[type]) {
				devicesList[type] = { ids: [] };
			}
			if (!devicesList[type].ids.includes(id)) {
				devicesList[type].ids.push(id);
			}
			return devicesList;
		}

		const index = get_device_component_index(d);
		const id = index > 0 ? `${d.id}_${index}` : `${d.id}`;
		const type = getDeviceGroupType(id);

		if (!devicesList[type]) {
			devicesList[type] = { ids: [] };
		}
		devicesList[type].ids.push(id);

		return devicesList;
	}, {});

	return tmpDevices;
};

//#region direction helpers

//#endregion

//#region effects helpers

//#endregion

//#region gain/brightness helpers

//#endregion

//#region mode helpers

//#endregion

//#region on/off helpers

//#endregion

//#region positioning helpers

//#endregion

//#region thermostat helpers

//#endregion

//#region white channel helpers

//#endregion
